import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import {
  faPaintbrush,
  faPlus,
  faSpinner,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import NiceModal from "@ebay/nice-modal-react";
import POSAEditorModal from "../POSAEditor/POSAEditorModal";
import { POSAMode } from "../POSAEditor/model";
import KC_Icon_Button from "../../../../designsystem/KC_Button/KC_Icon_Button";
import DownloadPOSAButton from "../../modules/DownloadPOSAButton/DownloadPOSAButton";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Text from "../../../../designsystem/typography/KC_Text/KC_Text";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PNGPreview from "../../../../components/SVGPreview/PNGPreview";
import { useSelector } from "react-redux";
import type { RootStateI } from "../../../../redux";
import { useCustomPOSASelector } from "../../../../redux/customPOSA";
import { tt } from "../../../../utils/i18n/i18n";
import { POSATypeJavascriptDTO } from "@tootsweet/model/lottery/POSAType";
import POSAApi from "../../../12_POSAManager/api";
import { useDispatch } from "react-redux";
import { removeCustomPOSA } from "../../../../redux/customPOSA";
import { setLoading } from "../../../../redux/actions/app";
import { CustomPOSA_Javascript_DTO } from "@tootsweet/model/entities/saas/CustomPOSA";
import { svgRenderer } from "../../../../utils/svgRenderer";
import { getWidthForPOSAFormat } from "kadow-svg-utils";

interface Props {
  posa: POSATypeJavascriptDTO;
  posaMode: POSAMode;
  eventId: string;
  handleSelected?: () => void;
  description?: string;
  customPOSA?: CustomPOSA_Javascript_DTO;
  customTitle?: string;
  disableDownload?: boolean;
  bordered?: boolean;
  isLoading?: boolean;
}

export default function POSAPreviewCard(props: Props) {
  const jetpackConfig = useJetpackConfigSelector();
  const dispatch = useDispatch();

  const currentPOSAOrder = useSelector(
    (state: RootStateI) => state.saas.currentPOSAOrder
  );
  const customPOSA = useCustomPOSASelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [dummy, setDummy] = useState(1);

  const showModifyButton = useMemo(() => {
    return !!props.posa.isCustom && !!accountToDisplay?.saasFeatures?.cp;
  }, [props.posa, accountToDisplay?.saasFeatures]);

  const isSelected = useMemo(() => {
    const posaTypeId = props.customPOSA?.posaTypeId || props.posa._id;
    const index =
      currentPOSAOrder.items?.findIndex((it) => it.posaTypeId === posaTypeId) ??
      -1;
    return index != -1;
  }, [props.customPOSA, props.posa, currentPOSAOrder]);

  const DEFAULT_WIDTH = useMemo(() => {
    return getWidthForPOSAFormat(props.posa.format);
  }, [props.posa]);

  const handleOpenEditModal = (
    posa: POSATypeJavascriptDTO,
    title: string,
    posaDestination: POSAMode,
    eventId: string,
    enableDownload: boolean
  ) => {
    void NiceModal.show(POSAEditorModal, {
      title,
      posaDestination,
      eventId,
      enableDownload,
      posa,
      svgID: svgRenderer.generateSVGId(posa._id),
      isNotCustomPOSA: !props.posa.isCustom,
      customPOSA: props.customPOSA
    });
  };

  const handleDeleteCustomPOSA = async () => {
    if (!props.customPOSA || !accountToDisplay?.token) {
      return;
    }

    try {
      dispatch(setLoading(true));
      await POSAApi.deleteCustomPOSA(
        props.customPOSA._id,
        accountToDisplay.token
      );
      dispatch(removeCustomPOSA(props.customPOSA.posaTypeId));
    } catch (error) {
      console.error("Failed to delete custom POSA:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const STYLE = useMemo<CSSProperties>(() => {
    return { width: DEFAULT_WIDTH };
  }, [DEFAULT_WIDTH]);

  /**
   * When the logo changes or the colors, the URL of the custom POSA PNG remains unchanged,
   * but its content has changed.
   * The images are stored in S3 with a "no-cache" policy, but to make them
   * refresh without refreshing the page, we increment a dummy number that will
   * be added to the image URL.
   */
  useEffect(() => {
    if (!props.customPOSA) {
      return;
    }
    setDummy(dummy + 1);
  }, [customPOSA]);

  return (
    <div className="d-flex flex-column gap-sm posa-preview-card" style={STYLE}>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ height: 24 }}
      >
        {props.customTitle ? (
          <KC_Heading3 text={props.customTitle} />
        ) : (
          <KC_Heading3
            text={tt("posa_model", {
              templateName: props.posa.shortId
            })}
          />
        )}
        {props.customPOSA && (
          <KC_Icon_Button
            icon={faTimes}
            size={KC_ButtonSize.Small}
            onClick={handleDeleteCustomPOSA}
            extraStyle={{ background: "transparent", border: "none" }}
          />
        )}
      </div>
      {props.description && (
        <KC_Text size="md" weight="reg" text={props.description} />
      )}
      <div className="w-100 position-relative">
        <PNGPreview
          customURL={props.posa.isCustom ? props.customPOSA?.url : undefined}
          dummy={props.customPOSA ? dummy : undefined}
          useTwicPics={!props.posa.isCustom}
          posa={props.posa}
          isBordered={true}
        />
      </div>

      <div className="d-flex justify-content-center align-items-center st-gap action-wrapper">
        {!props.posa.isOnline && (
          <KC_Button
            id={`${props.posaMode}-${props.posa._id}-select`}
            className={`d-flex ${isSelected ? "" : "kc-btn-border-black"}`}
            isPrimaryLight={isSelected}
            disabled={props.isLoading}
            showIconOnTheLeft={props.isLoading}
            icon={props.isLoading ? faSpinner : undefined}
            spinIcon={props.isLoading}
            text={
              isSelected ? (
                <>{tt("added_to_basket")}</>
              ) : props.isLoading ? (
                <>{tt("add_to_basket")}</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} /> {tt("add_to_basket")}
                </>
              )
            }
            isInverted={!isSelected}
            onClick={props.handleSelected}
          />
        )}
        {showModifyButton && (
          <KC_Icon_Button
            isInverted={true}
            icon={faPaintbrush}
            size={KC_ButtonSize.Medium}
            onClick={() => {
              handleOpenEditModal(
                props.posa,
                tt("edit_your_posa"),
                props.posaMode,
                props.eventId,
                false
              );
            }}
          />
        )}
        {!props.disableDownload && (
          <DownloadPOSAButton
            configId={jetpackConfig?.configId}
            customURL={props.customPOSA?.url}
            eventId={props.eventId}
            posa={props.posa}
          />
        )}
      </div>
    </div>
  );
}
